import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { ConfirmDialog } from './ConfirmDialog'

const UNSAVED_CHANGES_TITLE = '放弃未保存的改动？'
const UNSAVED_CHANGES_DESCRIPTION = '关闭后已填写的内容不会保留。'

function UnsavedChangesDialog({ open, onDiscard, onStay }: {
  open: boolean
  onDiscard: () => void
  onStay: () => void
}) {
  return (
    <ConfirmDialog
      open={open}
      title={UNSAVED_CHANGES_TITLE}
      description={UNSAVED_CHANGES_DESCRIPTION}
      confirmText="放弃改动"
      cancelText="继续编辑"
      danger
      onConfirm={onDiscard}
      onClose={onStay}
    />
  )
}

/** 浮层打开期间只要用户改过输入就标脏；关闭后清掉。不根据值快照，避免异步回填误报。 */
function useDirtyFlag(open: boolean) {
  const [dirty, setDirty] = useState(false)
  useEffect(() => {
    if (!open) setDirty(false)
  }, [open])
  const markDirty = useCallback(() => setDirty(true), [])
  return { dirty, markDirty }
}

export function useGuardedClose(dirty: boolean, onClose: () => void) {
  const [asking, setAsking] = useState(false)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!dirty) setAsking(false)
  }, [dirty])

  const requestClose = useCallback(() => {
    if (dirty) setAsking(true)
    else onClose()
  }, [dirty, onClose])

  const stay = useCallback(() => setAsking(false), [])
  const discard = useCallback(() => {
    setAsking(false)
    onCloseRef.current()
  }, [])

  const dialog: ReactNode = <UnsavedChangesDialog open={asking} onDiscard={discard} onStay={stay} />
  return { requestClose, dialog }
}

export function useSubmitLock() {
  const locked = useRef(false)
  const [pending, setPending] = useState(false)
  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    if (locked.current) return undefined
    locked.current = true
    setPending(true)
    try {
      return await fn()
    } finally {
      locked.current = false
      setPending(false)
    }
  }, [])
  return { pending, run }
}

/** 对话框/抽屉表单的最小接入：脏标记 + 关闭确认 + 提交锁。 */
export function useFormGuards(open: boolean, onClose: () => void) {
  const { dirty, markDirty } = useDirtyFlag(open)
  const { requestClose, dialog } = useGuardedClose(dirty, onClose)
  const { pending, run } = useSubmitLock()
  return { dirty, markDirty, requestClose, dialog, pending, run }
}

/**
 * 页面级离开保护（BrowserRouter 不能用 useBlocker）。
 * 脏时压一条同 URL 哨兵拦住后退，并挂 beforeunload 拦刷新/关标签。
 */
export function usePageLeaveGuard(dirty: boolean) {
  const sentinelPushed = useRef(false)
  const sentinelHref = useRef<string | null>(null)
  const skipNextGuard = useRef(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  useEffect(() => {
    if (!dirty) {
      if (sentinelPushed.current) {
        // why: 提交成功的 navigate(replace) 已覆盖哨兵时 URL 已变，再 back 会撤销跳转；URL 未变仍是放弃编辑，继续消费哨兵 (LeapWorldTwin-298)
        const replacedAway = sentinelHref.current !== null && window.location.href !== sentinelHref.current
        sentinelPushed.current = false
        sentinelHref.current = null
        if (replacedAway) return
        skipNextGuard.current = true
        window.history.back()
      }
      return
    }

    if (!sentinelPushed.current) {
      window.history.pushState({ bfUnsavedGuard: true }, '')
      sentinelPushed.current = true
      sentinelHref.current = window.location.href
    }

    const onPopState = () => {
      if (skipNextGuard.current) {
        skipNextGuard.current = false
        return
      }
      if (!sentinelPushed.current) return
      sentinelPushed.current = false
      setConfirmOpen(true)
    }

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = UNSAVED_CHANGES_DESCRIPTION
      return UNSAVED_CHANGES_DESCRIPTION
    }

    window.addEventListener('popstate', onPopState)
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      window.removeEventListener('popstate', onPopState)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [dirty])

  const confirmLeave = () => {
    setConfirmOpen(false)
    window.history.back()
  }

  const stay = () => {
    setConfirmOpen(false)
    window.history.pushState({ bfUnsavedGuard: true }, '')
    sentinelPushed.current = true
    sentinelHref.current = window.location.href
  }

  return {
    dialog: <UnsavedChangesDialog open={confirmOpen} onDiscard={confirmLeave} onStay={stay} />,
    confirmOpen,
    confirmLeave,
    stay
  }
}
